
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Instagram, MessageSquare, Twitter, Facebook, Linkedin, Mail, Copy, Hash, Award, MailCheck } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { CampaignSettings, MilestoneReward } from './types';
import { useToast } from '@/hooks/use-toast';

interface CampaignWidgetPreviewProps {
  settings: CampaignSettings;
}

const CampaignWidgetPreview: React.FC<CampaignWidgetPreviewProps> = ({
  settings
}) => {
  const referralLink = "https://refer.me/ABC123";
  const { toast } = useToast();
  const isMilestoneProgram = settings.milestoneRewards && settings.milestoneRewards.length > 0;
  const isNewsletterReferral = settings.campaignType === 'newsletter-referral';
  
  const getSocialIcon = (platform: string) => {
    switch (platform) {
      case 'instagram':
        return <Instagram className="h-5 w-5" />;
      case 'whatsapp':
        return <MessageSquare className="h-5 w-5" />; // Using MessageSquare as fallback for WhatsApp
      case 'twitter':
        return <Twitter className="h-5 w-5" />;
      case 'facebook':
        return <Facebook className="h-5 w-5" />;
      case 'linkedin':
        return <Linkedin className="h-5 w-5" />;
      case 'mail':
        return <Mail className="h-5 w-5" />;
      default:
        return null;
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink).then(() => {
      toast({ 
        title: "Link Copied", 
        description: "Referral link copied to clipboard"
      });
    });
  };

  // Function to find the next milestone based on current referral count
  const getNextMilestone = (currentCount: number) => {
    if (!settings.milestoneRewards || settings.milestoneRewards.length === 0) return null;
    
    const sortedMilestones = [...settings.milestoneRewards].sort((a, b) => a.threshold - b.threshold);
    return sortedMilestones.find(m => m.threshold > currentCount) || null;
  };

  // Mock current referrals count (would be fetched from API in real implementation)
  const currentReferrals = 0;
  const nextMilestone = getNextMilestone(currentReferrals);

  // Helper to render newsletter specific rewards section
  const renderNewsletterRewards = () => {
    if (!settings.milestoneRewards || settings.milestoneRewards.length === 0) return null;
    
    const sortedMilestones = [...settings.milestoneRewards].sort((a, b) => a.threshold - b.threshold);
    
    return (
      <div className="space-y-3 mt-4">
        <h3 className="text-sm font-medium flex items-center">
          <MailCheck className="h-4 w-4 mr-1 text-green-500" />
          Newsletter Referral Rewards
        </h3>
        <div className="space-y-2">
          {sortedMilestones.map((milestone) => (
            <div key={milestone.id} className="bg-gray-50 p-2 rounded-lg border text-xs flex justify-between items-center">
              <div className="flex items-center">
                <span className="font-medium mr-1">{milestone.threshold}</span>
                <span className="text-gray-500">
                  {milestone.rewardType === 'recurring' ? ' referrals every cycle' : ' total referrals'}
                </span>
              </div>
              <div>
                <span className="font-medium">{milestone.reward}</span>
                {milestone.isPercentage ? ' % discount' : ' discount'}
              </div>
            </div>
          ))}
        </div>
        <div className="text-xs text-gray-500 mt-1">
          You've referred <span className="font-medium">0</span> subscribers so far
        </div>
      </div>
    );
  };

  return (
    <div className="rounded-lg border bg-white p-1 shadow-sm">
      <div className="flex flex-col h-full bg-card">
        <Card className="border-0 shadow-none h-full">
          <CardHeader className="pb-2">
            <CardTitle className="text-xl">{settings.title}</CardTitle>
            <p className="text-sm text-gray-500">
              {settings.subtitle.replace("{{referralCode}}", "ABC123")}
            </p>
            {settings.globalCampaignId && (
              <div className="flex items-center text-xs text-gray-400 mt-1">
                <Hash className="h-3 w-3 mr-1" />
                <span className="font-mono">{settings.globalCampaignId}</span>
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Your Referral Link</label>
              <div className="flex">
                <Input 
                  value={referralLink} 
                  readOnly 
                  className="rounded-r-none" 
                />
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="rounded-l-none border-l-0"
                  onClick={handleCopyLink}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            {isNewsletterReferral ? (
              renderNewsletterRewards()
            ) : isMilestoneProgram && (
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center">
                  <Award className="h-4 w-4 mr-1 text-amber-500" />
                  Milestone Rewards
                </label>
                <div className="bg-gray-50 p-3 rounded-lg border text-sm">
                  <div className="flex justify-between mb-2">
                    <span className="font-medium">Next reward:</span>
                    <span>{nextMilestone ? `${nextMilestone.threshold} referrals - ${nextMilestone.reward}` : 'Complete all milestones'}</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    {nextMilestone && (
                      <div 
                        className="h-full bg-primary" 
                        style={{ 
                          width: `${nextMilestone ? (currentReferrals / nextMilestone.threshold) * 100 : 0}%` 
                        }}
                      />
                    )}
                  </div>
                  <div className="mt-1 text-xs text-gray-500 text-right">
                    {currentReferrals}/{nextMilestone ? nextMilestone.threshold : '?'} referrals
                  </div>
                </div>
              </div>
            )}
            
            {settings.shareOptions.length > 0 && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Share with Friends</label>
                <div className="flex flex-wrap gap-2">
                  {settings.shareOptions.map(platform => (
                    <Button
                      key={platform}
                      variant="outline"
                      size="icon"
                      className="rounded-full"
                      title={`Share via ${platform}`}
                    >
                      {getSocialIcon(platform)}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            
            <div className="pt-2 border-t">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold">0</p>
                  <p className="text-xs text-gray-500">Total Referrals</p>
                </div>
                <div>
                  <p className="text-2xl font-bold">0</p>
                  <p className="text-xs text-gray-500">Completed</p>
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {isMilestoneProgram ? (
                      <span className="text-amber-500">
                        <Award className="h-5 w-5 inline" />
                      </span>
                    ) : (
                      `$${settings.referrerReward.toFixed(2)}`
                    )}
                  </p>
                  <p className="text-xs text-gray-500">
                    {isMilestoneProgram ? 'Rewards' : 'Earnings'}
                  </p>
                </div>
              </div>
            </div>
            
            {settings.showAffiliatePortal && (
              <div className="text-center pt-2 mt-2 border-t">
                <Button variant="link" size="sm" className="text-xs">
                  Join our affiliate program
                </Button>
              </div>
            )}
            
            <div className="text-center text-xs text-gray-400 mt-2">
              Powered by ReferMe.site
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CampaignWidgetPreview;
